/**
 * This file contains the UserCardView and associated sub-components.
 *
 * The "user card" is the large rectangular element that displayed at the top
 * of a user's profile page, and includes the avatar, username, bio,
 * and other public data.
 */

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import classNames from "classnames";
import gql from "graphql-tag";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import Clickable from "@khanacademy/wonder-blocks-clickable";
import {View} from "@khanacademy/wonder-blocks-core";
import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {HeadingLarge, LabelSmall} from "@khanacademy/wonder-blocks-typography";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {pureSmMin} from "@khan/shared-styles";
import {staticUrl} from "@khan/urls";

import AvatarCustomizerModal from "#avatar-customizer-package/avatar-customizer-modal.tsx";
import ApolloQuery from "#data-access/gql/apollo-query.tsx";
import {hideExternalLinks} from "#discussion-package/util.tsx";
import type {ProfileModelType} from "#shared-package/profile-model.ts";

import {getAvatarDisplayName} from "./strings.ts";
import UserCardModal from "./user-card-modal.tsx";
import UserPointsAndBadges from "./user-points-and-badges.tsx";

type UserCardAvatarViewProps = {
    /**
     * The avatar that this privacy option is associated with.
     */
    avatarName: string;
    avatarSrc: string;
    onClick: () => unknown;
};

export const AVATAR_DATA_QUERY: GqlOperation<AvatarData, Variables> = gqlOp(gql`
    query avatarDataForProfile($kaid: String!) {
        user(kaid: $kaid) {
            id
            avatar {
                name
                imageSrc
            }
        }
    }
`);

const CHANGED_AVATAR_DATA_QUERY: GqlOperation<ChangedAvatarData, Variables> =
    gqlOp(gql`
        query hasChangedAvatar($kaid: String!) {
            user(kaid: $kaid) {
                id
                hasChangedAvatar
            }
        }
    `);

type AvatarData = {
    user?: {
        id: string;
        avatar: {
            name: string;
            imageSrc: string;
        };
    };
};
type ChangedAvatarData = {
    user?: {
        id: string;
        hasChangedAvatar: boolean;
    };
};
type Variables = {
    kaid: string;
};

/**
 * Renders the avatar in the user card
 */
class UserCardAvatarView extends React.Component<UserCardAvatarViewProps> {
    onKeyUpAvatar = (event: React.KeyboardEvent) => {
        if (event.key === "Enter" || event.key === " ") {
            this.props.onClick();
        }
    };

    render(): React.ReactNode {
        const {onClick, avatarSrc, avatarName} = this.props;
        const avatarIsEditable = !!onClick;

        const classes = classNames({
            "avatar-pic-container": true,
            editable: avatarIsEditable,
        });

        const avatarLabel = getAvatarDisplayName(avatarName);

        const altText = avatarIsEditable
            ? t`${avatarLabel} avatar. Click to change your avatar.`
            : t`${avatarLabel} avatar.`;

        return (
            // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- TODO: Address a11y error
            <div
                className={classes}
                style={{float: "left"}}
                onClick={onClick}
                role={avatarIsEditable ? "button" : ""}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- TODO: Address a11y error
                tabIndex={avatarIsEditable ? 0 : undefined}
                onKeyUp={avatarIsEditable ? this.onKeyUpAvatar : undefined}
            >
                <div className="user-avatar-background">
                    <img
                        src={staticUrl(avatarSrc)}
                        className="avatar-pic"
                        alt={altText}
                    />
                </div>
            </div>
        );
    }
}

type UserCardBasicInfoViewProps = {
    /**
     * The `ProfileModel` instance that this privacy option is
     * associated with.
     */
    profile: ProfileModelType;
};

/**
 * Renders the "basic info" in the user card (username, nickname/"real name",
 * and bio).  On click, opens a modal that allows users to edit that info.
 */
class UserCardBasicInfoView extends React.Component<UserCardBasicInfoViewProps> {
    render(): React.ReactNode {
        const fullyEditable = this.props.profile.isFullyEditable();

        // If a phantom has a proper "nickname" set, that means they're midway
        // through the signup process.
        // TODO(diedra): I don't think this is true anymore -
        // for phantoms without nicknames set,
        // `this.props.model.get("nickname")` returns "Learner".
        // See `get_default_nickname_for` in `nicknames.py`
        const showSignupButton =
            this.props.profile.get("isPhantom") &&
            !this.props.profile.get("nickname");
        let signupButtonEl = null;
        if (showSignupButton) {
            // @ts-expect-error - TS2322 - Type 'Element' is not assignable to type 'null'.
            signupButtonEl = (
                <div className="user-card-signup-button">
                    <a
                        href="/signup?continue=%2Fprofile"
                        className="simple-button green"
                    >
                        {t`Sign up to claim your profile`}
                    </a>
                </div>
            );
        }

        let usernameContents:
            | React.ReactElement<React.ComponentProps<"span">>
            | string = this.props.profile.usernameFormatted();
        if (!usernameContents && fullyEditable) {
            // Hide widget on mobile (< 568px)
            usernameContents = (
                <span className="empty-field pure-hidden-xs">
                    {t`Pick a username`}
                </span>
            );
        }

        // for the profile header, we do an async retrieval of full profile
        // info, so leave bio blank until that returns
        const loadingProfileHeader = !this.props.profile.get(
            "includesUserDataInfo",
        );

        const bioContentsFromProfile = this.props.profile.get("bio");
        const bioContents = bioContentsFromProfile ? (
            hideExternalLinks(bioContentsFromProfile)
        ) : fullyEditable && !loadingProfileHeader ? (
            // Hide widget on mobile (< 568px)
            <span
                className="empty-field pure-hidden-xs"
                style={{textDecoration: "underline"}}
            >
                {t`Add your bio`}
            </span>
        ) : undefined;

        return (
            <ModalLauncher
                modal={({closeModal}) => (
                    <UserCardModal
                        closeModal={closeModal}
                        kaid={this.props.profile.get("kaid")}
                        nickname={this.props.profile.get("nickname")}
                        username={this.props.profile.get("username")}
                        bio={this.props.profile.get("bio")}
                    />
                )}
            >
                {({openModal}) => (
                    <Clickable
                        onClick={openModal}
                        style={styles.userCardBasicInfoView}
                    >
                        {({hovered, focused, pressed, waiting}) => (
                            <View>
                                <View style={styles.nicknameContainer}>
                                    <HeadingLarge tag="h2">
                                        {this.props.profile.get("nickname")}
                                    </HeadingLarge>
                                    <LabelSmall
                                        style={styles.nicknameSubcontents}
                                    >
                                        <span>{usernameContents}</span>
                                        {" - "}
                                        <span>{bioContents}</span>
                                    </LabelSmall>
                                </View>
                                {signupButtonEl}
                            </View>
                        )}
                    </Clickable>
                )}
            </ModalLauncher>
        );
    }
}

type UserCardViewProps = {
    profile: ProfileModelType;
    showBadgeSummary: boolean;
};

type UserCardViewState = {
    showAvatarCustomizer: boolean;
    // We use this flag to render some non-critical information only
    // on the client, and not on the server.
    initialRender: boolean;
};

/**
 * Renders the "user card", the large rectangular element that displayed at
 * the top of a user's profile page. Includes the avatar, username, bio,
 * other public data, and "Follow" button.
 */
class UserCardView extends React.Component<
    UserCardViewProps,
    UserCardViewState
> {
    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted: boolean;
    _isUsernamePickerLoading: boolean | undefined;

    static defaultProps: {
        showBadgeSummary: boolean;
    } = {
        showBadgeSummary: true,
    };

    constructor(props: UserCardViewProps) {
        super(props);

        // EDIT(david, Aug. 11): avatarPicker: check! avatar-customizer.jsx
        this.state = {
            showAvatarCustomizer: false,
            // We use this flag to render some non-critical information only
            // on the client, and not on the server.
            initialRender: true,
        };
        this._isMounted = false;
    }

    componentDidMount() {
        this._isMounted = true;

        // Show the avatar customizer on query string match. This is so we can
        // have links from other pages or external sources open the avatar
        // customizer (e.g. emails or the nav bar notifications dropdown).
        //
        // NOTE(jordan): Hide the avatar customizer on mobile.
        // NOTE(boris): Hide avatar customizer if not on editable user.
        let showAvatarCustomizer = false;
        if (
            this.props.profile &&
            this.props.profile.isEditable() &&
            window.innerWidth > parseFloat(pureSmMin)
        ) {
            const params = new URLSearchParams(
                // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
                // eslint-disable-next-line no-restricted-syntax
                window.location.search,
            );
            showAvatarCustomizer = !!params.get("show_avatar_customizer");
        }

        this.setState({
            initialRender: false,
            showAvatarCustomizer,
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _onCustomizeAvatarClick: () => void = () => {
        this.setState({showAvatarCustomizer: true});
    };

    _onAvatarCustomizerClose: () => void = () => {
        this.setState({showAvatarCustomizer: false});
    };

    render(): React.ReactNode {
        let editMenuEl: React.ReactElement | null = null;
        if (this.props.profile.isFullyEditable()) {
            editMenuEl = (
                <ModalLauncher
                    modal={({closeModal}) => (
                        <UserCardModal
                            closeModal={closeModal}
                            kaid={this.props.profile.get("kaid")}
                            nickname={this.props.profile.get("nickname")}
                            username={this.props.profile.get("username")}
                            bio={this.props.profile.get("bio")}
                        />
                    )}
                >
                    {({openModal}) => (
                        <Button
                            size="small"
                            onClick={openModal}
                            style={styles.editProfileButton}
                            kind="secondary"
                        >
                            {t`Edit Profile`}
                        </Button>
                    )}
                </ModalLauncher>
            );
        }

        // The user-card-privacy-indicator is rendered later, in profile-
        // widgets.jsx
        // TODO(stephanie): perhaps have that happen here instead someday

        let userPointsAndBadges;
        // TODO (jessie): show points and badges to the public based on the
        // user's privacy settings
        if (this.props.showBadgeSummary && !this.state.initialRender) {
            userPointsAndBadges = (
                <UserPointsAndBadges profile={this.props.profile} />
            );
        }

        const basicInfo = (
            <View style={styles.basicUserInfo}>
                <ApolloQuery
                    query={AVATAR_DATA_QUERY}
                    variables={{kaid: this.props.profile.get("kaid")}}
                    fetchPolicy="cache-and-network"
                >
                    {({data}) => {
                        if (!data?.user) {
                            // TODO(csilvers): go back to using a View
                            // for `avatar-pic` once we've moved this
                            // component to use Aphrodite.  See
                            // https://github.com/Khan/webapp/pull/12821#issuecomment-1492029347
                            return (
                                <div
                                    className="user-info-container"
                                    style={{float: "left"}}
                                >
                                    <div className="avatar-pic" />
                                </div>
                            );
                        }
                        return (
                            <UserCardAvatarView
                                avatarName={data.user.avatar.name}
                                avatarSrc={data.user.avatar.imageSrc}
                                onClick={
                                    this.props.profile.isEditable()
                                        ? this._onCustomizeAvatarClick
                                        : () => {}
                                }
                            />
                        );
                    }}
                </ApolloQuery>
                <UserCardBasicInfoView profile={this.props.profile} />
            </View>
        );

        // The old profile has user-info-container in a handlebars
        // template, but this makes the switch from the large profile
        // header to the small profile header more clear
        return (
            <div className="user-info-container large-header">
                {this.state.showAvatarCustomizer && (
                    <ApolloQuery
                        query={CHANGED_AVATAR_DATA_QUERY}
                        variables={{kaid: this.props.profile.get("kaid")}}
                        fetchPolicy="cache-and-network"
                    >
                        {({loading, data}) => {
                            return (
                                <AvatarCustomizerModal
                                    onClose={this._onAvatarCustomizerClose}
                                    hasChangedAvatar={
                                        !loading && data?.user?.hasChangedAvatar
                                    }
                                />
                            );
                        }}
                    </ApolloQuery>
                )}
                <div className="user-info clearfix">
                    <div className="user-info-all">
                        {basicInfo}
                        {/* Hide button on < 568px */}
                        <div className="user-card-edit-menu pure-hidden-xs">
                            {editMenuEl}
                        </div>
                        {userPointsAndBadges}
                    </div>
                </div>
            </div>
        );
    }
}

const styles = StyleSheet.create({
    nicknameContainer: {
        color: color.offBlack,
        alignItems: "start",
    },
    nicknameSubcontents: {
        marginTop: 2,
    },
    editProfileButton: {
        minWidth: 125,
        marginTop: 24,
    },
    userCardBasicInfoView: {
        cursor: "pointer",
        maxWidth: 767,
        maxHeight: 115,
        ":hover": {
            backgroundColor: color.offWhite,
        },
    },
    basicUserInfo: {
        flexDirection: "row",
        padding: 10,
    },
});

export default UserCardView;
